import { batch } from 'react-redux';

import { handleErrors } from '../../actions/ErrorActions';
import { addSuccess } from '../../actions/SuccessActions';
import { clearWarnings, enableWarnings, updateAllWarnings } from '../../actions/WarningActions';
import { deleteSavedSearch as deleteSavedSearchAPI, saveSavedSearch } from '../../api/saved-search';
import {
  getSaveAction,
  getSavedSearchForSaveAs,
  modifySavedSearchOnSave,
} from '../utils/common.utils';
import { validateSavedSearchOnSave } from '../utils/validation.utils';

export const LOAD_SAVED_SEARCHES = 'LOAD_SAVED_SEARCHES';
export const LOAD_ENTITY_FIELDS = 'LOAD_ENTITY_FIELDS';
export const CLEAR_SAVED_SEARCH_DESIGNER = 'CLEAR_SAVED_SEARCH_DESIGNER';
export const EDIT_SAVED_SEARCH = 'EDIT_SAVED_SEARCH';
export const ADD_TABLE_ROW = 'ADD_TABLE_ROW';
export const EDIT_TABLE_ROW = 'EDIT_TABLE_ROW';
export const DELETE_TABLE_ROW = 'DELETE_TABLE_ROW';
export const MOVE_TABLE_ROW = 'MOVE_TABLE_ROW';
export const SET_SELECTED_TABLE_ROW = 'SET_SELECTED_TABLE_ROW';
export const ADD_DISTRIBUTION_LIST_ITEM = 'ADD_DISTRIBUTION_LIST_ITEM';
export const ADD_DISTRIBUTION_LIST_ITEMS = 'ADD_DISTRIBUTION_LIST_ITEMS';
export const DELETE_DISTRIBUTION_LIST_ITEM = 'DELETE_DISTRIBUTION_LIST_ITEM';
export const ADD_VALIDATION_ERRORS = 'ADD_VALIDATION_ERRORS';
export const DELETE_VALIDATION_ERRORS = 'DELETE_VALIDATION_ERRORS';
export const SET_SELECTED_SAVED_SEARCH_ID = 'SET_SELECTED_SAVED_SEARCH_ID';
export const ADD_SAVED_SEARCH = 'ADD_SAVED_SEARCH';
export const COPY_SAVED_SEARCH = 'COPY_SAVED_SEARCH';
export const DELETE_SAVED_SEARCH_NODE = 'DELETE_SAVED_SEARCH_NODE';

export const loadSavedSearches = (data) => ({
  type: LOAD_SAVED_SEARCHES,
  payload: data,
});

export const loadEntityFields = (data) => ({
  type: LOAD_ENTITY_FIELDS,
  payload: data,
});

export const clearSavedSearch = () => ({
  type: CLEAR_SAVED_SEARCH_DESIGNER,
});

export const editSavedSearch = (data) => ({
  type: EDIT_SAVED_SEARCH,
  payload: data,
});

export const addTableRow = (data) => ({
  type: ADD_TABLE_ROW,
  payload: data,
});

export const addSavedSearch = (data) => ({
  type: ADD_SAVED_SEARCH,
  payload: data,
});

export const copySavedSearch = (data) => ({
  type: COPY_SAVED_SEARCH,
  payload: data,
});

export const deleteSavedSearchNode = (data) => ({
  type: DELETE_SAVED_SEARCH_NODE,
  payload: data,
});

export const editTableRow = (data) => ({
  type: EDIT_TABLE_ROW,
  payload: data,
});

export const deleteTableRow = (data) => ({
  type: DELETE_TABLE_ROW,
  payload: data,
});

export const moveTableRow = (data) => ({
  type: MOVE_TABLE_ROW,
  payload: data,
});

export const addDistributionListItem = (data) => ({
  type: ADD_DISTRIBUTION_LIST_ITEM,
  payload: data,
});

export const addDistributionListItems = (data) => ({
  type: ADD_DISTRIBUTION_LIST_ITEMS,
  payload: data,
});

export const deleteDistributionListItem = (data) => ({
  type: DELETE_DISTRIBUTION_LIST_ITEM,
  payload: data,
});

export const addValidationErrors = (data) => ({
  type: ADD_VALIDATION_ERRORS,
  payload: data,
});

export const deleteValidationErrors = () => ({
  type: DELETE_VALIDATION_ERRORS,
});

export const setSelectedSavedSearchId = (searchId) => ({
  type: SET_SELECTED_SAVED_SEARCH_ID,
  payload: { searchId },
});

export const setSelectedRow = (data) => ({
  type: SET_SELECTED_TABLE_ROW,
  payload: data,
});

export const validateSavedSearch = ({ validateSearchName = false } = {}) => async (
  dispatch,
  getState
) => {
  try {
    const { savedSearches, isDocRepository } = getState().savedSearchDesigner;
    const { translations } = getState();

    dispatch(clearWarnings());
    dispatch(deleteValidationErrors());

    const validationErrors = await validateSavedSearchOnSave({
      validateSearchName,
      translations,
      savedSearches,
      isDocRepository,
    });

    if (validationErrors.length) {
      dispatch(addValidationErrors(validationErrors));
      dispatch(updateAllWarnings(validationErrors));
      dispatch(enableWarnings());
    }

    return !validationErrors.length;
  } catch (error) {
    throw new Error(`validateSavedSearch() failed: ${error}`);
  }
};

export const saveSavedSearchDesigner = ({
  setIsLoading,
  isSaveAs,
  isNew,
  newSavedSearchName,
  redirectToPrevPage,
  redirectToSavedSearch,
}) => async (dispatch, getState) => {
  try {
    const isValid = await dispatch(validateSavedSearch({ validateSearchName: !isSaveAs }));

    if (!isValid) {
      return;
    }

    const {
      savedSearches,
      entityFields,
      validationErrors,
      selectedSavedSearchId,
    } = getState().savedSearchDesigner;
    const savedSearch = savedSearches[selectedSavedSearchId].savedSearch;

    if (validationErrors?.length) {
      return;
    }

    setIsLoading(true);

    const modifiedSavedSearch = modifySavedSearchOnSave(savedSearch, entityFields);

    const response = await saveSavedSearch({
      saveAction: getSaveAction({ isNew, isSaveAs }),
      savedSearch: isSaveAs
        ? getSavedSearchForSaveAs({ savedSearch: modifiedSavedSearch, newSavedSearchName })
        : modifiedSavedSearch,
    });

    setIsLoading(false);

    if (!response.hasError) {
      const savedSearchId = response.savedSearch.id;

      batch(() => {
        dispatch(addSuccess({ message: 'Saved Search Saved Successfully' }));
        dispatch(
          loadSavedSearches({
            savedSearches: {
              [savedSearchId]: { savedSearch: response.savedSearch },
            },
            selectedSavedSearchId: savedSearchId,
          })
        );
      });

      isSaveAs
        ? redirectToSavedSearch({ savedSearchId, entityName: response.savedSearch.entityName })
        : redirectToPrevPage();
    } else if (Array.isArray(response.errors)) {
      dispatch(handleErrors(response.errors, { isTopBanner: true }));
    }
  } catch (error) {
    throw new Error(`saveSavedSearchDesigner() failed: ${error}`);
  }
};

export const deleteSavedSearch = ({ redirectToPrevPage }) => async (dispatch, getState) => {
  try {
    const { savedSearches, selectedSavedSearchId } = getState().savedSearchDesigner;
    const savedSearch = savedSearches[selectedSavedSearchId].savedSearch;

    const response = await deleteSavedSearchAPI({
      id: savedSearch.id,
      entityName: savedSearch.entityName,
    });

    if (!response.hasError) {
      dispatch(addSuccess({ message: 'Saved Search Deleted Successfully' }));
      redirectToPrevPage();
    } else if (Array.isArray(response.errors)) {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`deleteSavedSearch() failed: ${error}`);
  }
};
